import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { all } from "redux-saga/effects";

import { LoadStatus, type BaseMeta } from "@ds/utils/reducer";

import { watchFeatures } from "./sagas";

export enum FeaturesRemoteOperation {
  FETCH = "fetch",
}

export type FeaturesMeta = BaseMeta & {
  remoteOperation: FeaturesRemoteOperation;
  projectId: number;
};

export interface FeaturesState {
  loadStatusMap: {
    [FeaturesRemoteOperation.FETCH]: LoadStatus;
  };

  error: string | null;

  byProjectId: Record<number, Feature[]>;
}

export const initialState: FeaturesState = {
  loadStatusMap: {
    [FeaturesRemoteOperation.FETCH]: LoadStatus.Idle,
  },

  error: null,

  byProjectId: {},
};

const sliceType = "FEATURES";
const slice = createSlice({
  name: sliceType,
  initialState,
  reducers: {
    fetchFeatures: {
      reducer(state, { meta }: PayloadAction<undefined, string, FeaturesMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Loading;
        state.error = null;
      },
      prepare(projectId: number, remoteOperation: FeaturesRemoteOperation = FeaturesRemoteOperation.FETCH) {
        return { payload: undefined, meta: { remoteOperation, projectId } };
      },
    },
    fetchFeaturesFailed: {
      reducer(state, { meta, error }: PayloadAction<undefined, string, FeaturesMeta, string>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Failed;
        state.error = error;
      },
      prepare(meta: FeaturesMeta, { message }: ErrorLike) {
        return { payload: undefined, meta, error: message };
      },
    },
    fetchFeaturesSucceeded: {
      reducer(state, { payload, meta }: PayloadAction<Feature[], string, FeaturesMeta>) {
        state.loadStatusMap[meta.remoteOperation] = LoadStatus.Succeeded;
        state.error = null;

        state.byProjectId[meta.projectId] = payload;
      },
      prepare(payload: Feature[], meta: FeaturesMeta) {
        return { payload, meta };
      },
    },
  },
});

export const { name: featuresType, actions: featuresActions, reducer: featuresReducer } = slice;

export function* rootFeaturesSaga() {
  yield all([watchFeatures()]);
}
